import "./styles.scss";

import { ButtonVariant } from "@amzn/stencil-react-components/button";
import { FormWrapper } from "@amzn/stencil-react-components/form";
import { Col, GridContainer, GridItem, Row } from "@amzn/stencil-react-components/layout";
import { MessageBannerType } from "@amzn/stencil-react-components/message-banner";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import { Button } from "@amzn/uxm-stencil-react-components/lib/src/stencil/button";
import { Paths } from "common/paths/paths";
import { getString } from "common/uistringlabels/uiStringUtils";
import React, { FC, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";

import { useMessageBannersBox } from "../MessageBannersBox/hook";
import {
  AiForMeFormData,
  DataClassification,
  PlatformName,
  SecurityReviewSystem,
  YesNo,
} from "./constants/platform-constants";
import { editTool, submitTool } from "./helpers/tool-submission-helper";
import { ReviewField } from "./ReviewField";
import { ReviewSection } from "./ReviewSection";

export interface AiForMeReviewAndSubmitProps {
  formData: AiForMeFormData | null;
  onBack: (data?: Partial<AiForMeFormData>) => void;
  isEdit: boolean;
}

export const AiForMeReviewAndSubmit: FC<AiForMeReviewAndSubmitProps> = ({ formData, onBack, isEdit }) => {
  const history = useHistory();
  const { addErrorBanner, addMessageBanner } = useMessageBannersBox();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const formatList = (items: string[]) => {
    if (!items || items.length === 0) return "";
    return items.join(", ");
  };

  const formatUrl = (url: string) => (
    <Text fontSize="T150" className="review-url-link">
      <a href={url} target="_blank" rel="noopener noreferrer">
        {url}
      </a>
    </Text>
  );

  const handleToolSubmission = async (taxonomyId?: string) => {
    if (!formData) {
      addErrorBanner(isEdit ? getString("aiForMe.missingData") : "No form data available to submit");
      return;
    }

    const callbacks = {
      addErrorBanner,
      addMessageBanner,
      navigateToSuccess: () => history.push(isEdit ? Paths.aiformeMyTools.url : Paths.aiforme.url),
      setIsLoading,
    };

    const isCustomBuilt = formData.platformName === PlatformName.MODEL_HOSTING;
    const isThirdParty = formData.platformName === PlatformName.THIRD_PARTY;

    if (isEdit) {
      if (!taxonomyId) {
        addErrorBanner(getString("aiForMe.missingData"));
        return;
      }
      await editTool(formData, taxonomyId, callbacks, isCustomBuilt, isThirdParty);
    } else {
      await submitTool(formData, callbacks, isCustomBuilt, isThirdParty);
    }
  };

  const handleSubmit = () => {
    handleToolSubmission(formData?.taxonomyId);
  };

  const handleBackClick = () => {
    onBack(formData || undefined);
  };

  const shouldShowCommonSection = (): boolean => {
    if (!formData) return false;
    return (
      formData.securityReviewed === YesNo.NO ||
      (formData.securityReviewed === YesNo.YES && formData.securityReviewSystem === SecurityReviewSystem.TALOS)
    );
  };

  return (
    <GridContainer>
      <GridItem lg={3} md={2}>
        &nbsp;
      </GridItem>
      <GridItem lg={6} md={4}>
        <FormWrapper width={"100%"}>
          <Col rowGap={"30px"}>
            <Text fontSize="T400" fontWeight="bold" data-cy="review-page-title">
              Review Your Submission
            </Text>
            <Text fontSize="T100" className="review-subtitle" data-cy="review-page-subtitle">
              Please review all information below before submitting your AI tool.
            </Text>

            <ReviewSection title="Basic Information" data-cy="review-basic-information-section">
              <ReviewField label="Tool Name:" value={formData?.title || ""} data-cy="review-tool-name" />
              <ReviewField
                label="Tool Description:"
                value={formData?.description || ""}
                data-cy="review-tool-description"
                maxLength={200}
              />
              <ReviewField
                label="Tool URL:"
                value={formData?.directToolUrl ? formatUrl(formData.directToolUrl) : ""}
                data-cy="review-tool-url"
              />
              {formData?.documentationUrl && (
                <ReviewField
                  label="Documentation URL:"
                  value={formatUrl(formData.documentationUrl)}
                  data-cy="review-documentation-url"
                />
              )}
              {formData?.selectedCategories && formData.selectedCategories.length > 0 && (
                <ReviewField
                  label="Categories:"
                  value={formatList(formData.selectedCategories)}
                  data-cy="review-categories"
                />
              )}
              {formData?.selectedJobFamilies && formData.selectedJobFamilies.length > 0 && (
                <ReviewField
                  label="Job Families:"
                  value={formatList(formData.selectedJobFamilies)}
                  data-cy="review-job-families"
                />
              )}
            </ReviewSection>

            <ReviewSection title="Security Information" data-cy="review-security-information-section">
              <ReviewField label="Platform Type:" value={formData?.platformName || ""} data-cy="review-platform-type" />
              <ReviewField label="Tool Maturity:" value={formData?.toolMaturity || ""} data-cy="review-tool-maturity" />
              {formData?.dataClassification !== DataClassification.UNKNOWN && (
                <ReviewField
                  label="Data Classification:"
                  value={formData?.dataClassification || ""}
                  data-cy="review-data-classification"
                />
              )}
            </ReviewSection>

            {/* Show Custom Built Tool Details section only for custom built tools */}
            {formData?.platformName === PlatformName.MODEL_HOSTING && (
              <ReviewSection title="Custom Built Tool Details" data-cy="review-custom-built-section">
                <ReviewField
                  label="Security Review:"
                  value={formData?.securityReviewed || ""}
                  data-cy="review-security-review"
                />
                {formData?.securityReviewed && formData?.securityReviewLink && (
                  <ReviewField
                    label="Security Review Link:"
                    value={formatUrl(formData.securityReviewLink)}
                    data-cy="review-security-review-link"
                  />
                )}
                {shouldShowCommonSection() && (
                  <ReviewField
                    label="Models Used:"
                    value={formatList(formData?.modelsUsed || [])}
                    data-cy="review-models-used"
                  />
                )}
                {shouldShowCommonSection() && formData?.otherModel && (
                  <ReviewField label="Other Model:" value={formData.otherModel} data-cy="review-other-model" />
                )}
                {formData?.securityReviewed === YesNo.NO && (
                  <ReviewField
                    label="Store Input Prompts:"
                    value={formData?.storesInputPrompts || ""}
                    data-cy="review-input-prompts"
                  />
                )}
                {formData?.securityReviewed === YesNo.NO && (
                  <ReviewField
                    label="Data for Retraining:"
                    value={formData?.collectsDataForRetraining || ""}
                    data-cy="review-collects-data-retraining"
                  />
                )}
                {formData?.username && shouldShowCommonSection() && (
                  <ReviewField label="Tool Owner Alias:" value={formData.username} data-cy="review-tool-owner-alias" />
                )}
                {shouldShowCommonSection() && (
                  <ReviewField label="Tool Bindle:" value={formData?.toolBindle || ""} data-cy="review-tool-bindle" />
                )}
                {formData?.toolCTILink && shouldShowCommonSection() && (
                  <ReviewField label="CTI:" value={formData.toolCTILink} data-cy="review-cti" />
                )}
              </ReviewSection>
            )}

            {/* Show Third-Party Tool Details section only for third-party tools */}
            {formData?.platformName === PlatformName.THIRD_PARTY && (
              <ReviewSection title="Third-Party Tool Details" data-cy="review-third-party-section">
                <ReviewField label="Vendor Name:" value={formData?.vendorName || ""} data-cy="review-vendor-name" />
                <ReviewField
                  label="Business Justification:"
                  value={formData?.businessJustification || ""}
                  data-cy="review-business-justification"
                />
                {formData?.toolBindle && (
                  <ReviewField label="Tool Bindle:" value={formData.toolBindle} data-cy="review-tool-bindle" />
                )}
              </ReviewSection>
            )}

            <Row justifyContent="space-between" columnGap={5}>
              <Button
                variant={ButtonVariant.Secondary}
                onClick={() => history.push(Paths.aiforme.url)}
                disabled={isLoading}
                touchpoint="review-cancel"
              >
                {getString("aiForMe.cancel")}
              </Button>
              <Row columnGap={5}>
                <Button
                  variant={ButtonVariant.Secondary}
                  onClick={handleBackClick}
                  disabled={isLoading}
                  touchpoint="review-go-back"
                  data-cy="review-and-submit-go-back-button"
                >
                  {getString("aiForMe.previousPage")}
                </Button>
                <Button
                  variant={ButtonVariant.Primary}
                  onClick={handleSubmit}
                  disabled={isLoading}
                  touchpoint="review-submit-tool"
                  data-cy="submit-tool-button"
                >
                  {isLoading && <Spinner size={SpinnerSize.Small} />}
                  {!isLoading && getString("aiForMe.submitTool")}
                </Button>
              </Row>
            </Row>
          </Col>
        </FormWrapper>
      </GridItem>
      <GridItem lg={3} md={2}>
        &nbsp;
      </GridItem>
    </GridContainer>
  );
};
